import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ShieldCheck, ShieldAlert, Loader2, Wand2, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

/**
 * Live deliverability check for an editable email draft. Debounces a call
 * to /api/email/spam-preflight whenever subject/body change. Click the
 * chip to expand the fix panel — auto-fix (regex), AI rewrite (Claude),
 * or just see the trigger list.
 *
 * Props:
 *   subject       — string
 *   bodyHtml      — string
 *   onChange      — (newSubject, newBodyHtml) => void  (called when fix
 *                   buttons rewrite the email)
 *   variant       — 'inline' (default, small chip) | 'block' (full-width)
 *   testId        — base data-testid
 */
export default function SpamCheckChip({
  subject = '',
  bodyHtml = '',
  onChange,
  variant = 'inline',
  testId = 'spamcheck',
}) {
  const [warnings, setWarnings] = useState([]);
  const [severity, setSeverity] = useState('ok');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const debRef = useRef(null);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!subject && !bodyHtml) {
      setWarnings([]);
      setSeverity('ok');
      return;
    }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(
          `${API}/email/spam-preflight`,
          { subject, body_html: bodyHtml },
          { withCredentials: true },
        );
        setWarnings(data.warnings || []);
        setSeverity(data.severity || 'ok');
      } catch { /* silent — chip stays in last-known state */ }
      finally { setLoading(false); }
    }, 600);
    return () => {
      if (debRef.current) clearTimeout(debRef.current);
    };
  }, [subject, bodyHtml]);

  const autoFix = async () => {
    if (!onChange) {
      toast.error('Auto-fix unavailable in read-only mode');
      return;
    }
    setFixing(true);
    try {
      const { data } = await axios.post(
        `${API}/email/auto-fix`,
        { subject, body_html: bodyHtml },
        { withCredentials: true },
      );
      onChange(data.subject, data.body_html);
      const fixCount = (data.changes || []).reduce((acc, c) => acc + (c.count || 0), 0);
      if (fixCount > 0) {
        toast.success(`Auto-fixed ${fixCount} trigger phrase${fixCount !== 1 ? 's' : ''}`);
      } else {
        toast.info('No deterministic swaps applicable — try AI rewrite');
      }
      setWarnings(data.warnings || []);
      setSeverity(data.severity || 'ok');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-fix failed');
    } finally { setFixing(false); }
  };

  const aiRewrite = async () => {
    if (!onChange) return;
    setRewriting(true);
    try {
      const { data } = await axios.post(
        `${API}/email/rewrite-clean`,
        { subject, body_html: bodyHtml, warnings },
        { withCredentials: true, timeout: 30000 },
      );
      onChange(data.subject, data.body_html);
      toast.success(data.model_notes || 'Rewritten by AI');
      setWarnings(data.warnings || []);
      setSeverity(data.severity || 'ok');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI rewrite failed');
    } finally { setRewriting(false); }
  };

  // ----- chip -----
  const sevColor = {
    ok:     { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',  label: 'Deliverability OK' },
    medium: { chip: 'bg-amber-50 text-amber-800 border-amber-300',       label: `${warnings.length} warning${warnings.length !== 1 ? 's' : ''}` },
    high:   { chip: 'bg-rose-50 text-rose-800 border-rose-300',          label: `${warnings.length} warnings` },
  }[severity];

  const Icon = severity === 'ok' ? ShieldCheck : ShieldAlert;

  if (variant === 'block') {
    return (
      <div className={`border rounded-lg ${sevColor.chip} px-3 py-2`} data-testid={`${testId}-block`}>
        <div className="flex items-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
          <span className="text-xs font-medium">{sevColor.label}</span>
          {warnings.length > 0 && (
            <button
              onClick={() => setOpen(o => !o)}
              className="ml-auto text-[10px] underline-offset-2 hover:underline"
              data-testid={`${testId}-toggle`}
            >
              {open ? 'Hide' : 'Show details'}
            </button>
          )}
        </div>
        {open && warnings.length > 0 && <FixPanel warnings={warnings} fixing={fixing} rewriting={rewriting} autoFix={autoFix} aiRewrite={aiRewrite} testId={testId} />}
      </div>
    );
  }

  // Inline variant
  return (
    <span className="relative inline-block" data-testid={`${testId}-inline`}>
      <button
        type="button"
        onClick={() => warnings.length > 0 && setOpen(o => !o)}
        disabled={warnings.length === 0 && severity === 'ok'}
        className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${sevColor.chip} ${warnings.length > 0 ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
        data-testid={`${testId}-chip`}
        title={warnings.length > 0 ? `Click to see ${warnings.length} deliverability warning(s)` : 'Email looks clean'}
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
        {sevColor.label}
      </button>
      {open && warnings.length > 0 && (
        <div className="absolute z-30 right-0 mt-1 w-80 bg-white border border-slate-200 rounded-lg shadow-lg p-3" data-testid={`${testId}-popover`}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="text-xs font-semibold text-slate-700">Deliverability triggers</p>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="w-3.5 h-3.5" /></button>
          </div>
          <FixPanel warnings={warnings} fixing={fixing} rewriting={rewriting} autoFix={autoFix} aiRewrite={aiRewrite} testId={testId} compact />
        </div>
      )}
    </span>
  );
}


function FixPanel({ warnings, fixing, rewriting, autoFix, aiRewrite, testId, compact = false }) {
  return (
    <div className={compact ? 'space-y-2' : 'mt-2 space-y-2'} data-testid={`${testId}-fixpanel`}>
      <ul className="text-[11px] text-slate-700 space-y-0.5 list-disc pl-4">
        {warnings.map((w, i) => <li key={i}>{w}</li>)}
      </ul>
      <div className="flex flex-wrap gap-1.5 pt-1">
        <Button
          variant="outline"
          size="sm"
          onClick={autoFix}
          disabled={fixing || rewriting}
          className="h-7 text-[10px] border-slate-300"
          data-testid={`${testId}-autofix-btn`}
        >
          {fixing ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Fixing…</> : <><Wand2 className="w-3 h-3 mr-1" /> Auto-fix</>}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={aiRewrite}
          disabled={fixing || rewriting}
          className="h-7 text-[10px] border-violet-300 text-violet-700 hover:bg-violet-50"
          data-testid={`${testId}-rewrite-btn`}
        >
          {rewriting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Rewriting…</> : <><Sparkles className="w-3 h-3 mr-1" /> AI rewrite</>}
        </Button>
      </div>
    </div>
  );
}


/**
 * Bulk triage queue for the AI Mapping / Bulk Email path. Given an array
 * of { id, subject, body, ...meta }, runs preflight on each in parallel
 * and renders a triage table with per-row Auto-fix / Rewrite / Edit /
 * Skip actions plus a "Fix all" sweeping action.
 *
 * Props:
 *   items     — Array<{ id, subject, body, label?, ...passThrough }>
 *   onChange  — (id, { subject, body }) => void
 *   onSkip    — (id) => void
 *   testId    — base test id
 */
export function SpamTriagePanel({ items = [], onChange, onSkip, pendingNote = '', testId = 'spam-triage' }) {
  const [reports, setReports] = useState({}); // id -> { warnings, severity }
  const [loading, setLoading] = useState(false);
  const [scanProgress, setScanProgress] = useState({ done: 0, total: 0 });
  const [fixAllProgress, setFixAllProgress] = useState(null); // { done, total, ok } while "Fix all" runs

  const runScan = async () => {
    setLoading(true);
    setScanProgress({ done: 0, total: items.length });
    const next = {};
    let done = 0;
    await Promise.all(items.map(async (it) => {
      try {
        const { data } = await axios.post(
          `${API}/email/spam-preflight`,
          { subject: it.subject, body_html: it.body },
          { withCredentials: true },
        );
        next[it.id] = { warnings: data.warnings || [], severity: data.severity };
      } catch {
        next[it.id] = { warnings: [], severity: 'ok' };
      }
      done += 1;
      setScanProgress({ done, total: items.length });
    }));
    setReports(next);
    setLoading(false);
  };

  useEffect(() => {
    if (items.length > 0) runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const flagged = items.filter(it => (reports[it.id]?.warnings?.length || 0) > 0);
  const clean = items.length - flagged.length;

  const fixAllAi = async () => {
    if (flagged.length === 0 || fixAllProgress) return;
    // ~8s per rewrite at concurrency 3
    const estSec = Math.ceil(flagged.length * 8 / 3);
    if (!window.confirm(`Send ${flagged.length} flagged email(s) through AI rewrite? Takes ~${estSec}s — progress shows on the button, keep this dialog open.`)) return;
    setFixAllProgress({ done: 0, total: flagged.length, ok: 0 });
    let ok = 0;
    let done = 0;
    // Process in small batches — firing all rewrites at once overwhelms the
    // LLM (rate limits + 30s timeouts) and silently rewrote nothing.
    const CONCURRENCY = 3;
    const queue = [...flagged];
    const worker = async () => {
      while (queue.length > 0) {
        const it = queue.shift();
        if (!it) break;
        try {
          const { data } = await axios.post(
            `${API}/email/rewrite-clean`,
            { subject: it.subject, body_html: it.body, warnings: reports[it.id]?.warnings || [] },
            { withCredentials: true, timeout: 60000 },
          );
          onChange?.(it.id, { subject: data.subject, body: data.body_html });
          setReports(prev => ({ ...prev, [it.id]: { warnings: data.warnings || [], severity: data.severity || 'ok' } }));
          // Count as fixed ONLY if the rewrite actually cleared every trigger —
          // a successful call that still has warnings stays flagged.
          if ((data.warnings || []).length === 0) ok += 1;
        } catch { /* row stays flagged — user can retry it individually */ }
        done += 1;
        setFixAllProgress({ done, total: flagged.length, ok });
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, flagged.length) }, worker));
    setFixAllProgress(null);
    const failed = flagged.length - ok;
    if (failed === 0) {
      toast.success(`Rewrote all ${ok} email${ok !== 1 ? 's' : ''} — flags cleared`);
    } else {
      toast.warning(`Cleared ${ok} of ${flagged.length} — ${failed} still flagged (rewrite didn't remove every trigger). Click "Fix all with AI" again to retry just those.`, { duration: 10000 });
    }
  };

  const fixOneAuto = async (it) => {
    try {
      const { data } = await axios.post(
        `${API}/email/auto-fix`,
        { subject: it.subject, body_html: it.body },
        { withCredentials: true },
      );
      onChange?.(it.id, { subject: data.subject, body: data.body_html });
      setReports(prev => ({ ...prev, [it.id]: { warnings: data.warnings || [], severity: data.severity || 'ok' } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-fix failed');
    }
  };

  const fixOneAi = async (it) => {
    try {
      const { data } = await axios.post(
        `${API}/email/rewrite-clean`,
        { subject: it.subject, body_html: it.body, warnings: reports[it.id]?.warnings || [] },
        { withCredentials: true, timeout: 30000 },
      );
      onChange?.(it.id, { subject: data.subject, body: data.body_html });
      setReports(prev => ({ ...prev, [it.id]: { warnings: data.warnings || [], severity: data.severity || 'ok' } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI rewrite failed');
    }
  };

  if (items.length === 0) return null;
  return (
    <div className="border border-slate-200 rounded-lg bg-white p-3 space-y-2" data-testid={`${testId}-panel`}>
      <div className="flex flex-wrap items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-semibold text-slate-800">Deliverability triage</span>
        {loading ? (
          <Badge variant="outline" className="text-[10px]">
            <Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" />
            Scanning {scanProgress.done}/{scanProgress.total}
          </Badge>
        ) : (
          <>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" variant="outline">{clean} clean</Badge>
            {flagged.length > 0 && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" variant="outline">{flagged.length} flagged</Badge>
            )}
            {pendingNote && (
              <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[10px]" variant="outline" data-testid={`${testId}-pending-note`}>{pendingNote}</Badge>
            )}
          </>
        )}
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={runScan} disabled={loading} className="h-7 text-[10px]" data-testid={`${testId}-rescan`}>
          Rescan
        </Button>
        {flagged.length > 0 && (
          <Button size="sm" onClick={fixAllAi} disabled={!!fixAllProgress} className="h-7 text-[10px] bg-violet-600 hover:bg-violet-700" data-testid={`${testId}-fix-all-ai`}>
            {fixAllProgress
              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Fixing {fixAllProgress.done}/{fixAllProgress.total}…</>
              : <><Sparkles className="w-3 h-3 mr-1" /> Fix all with AI</>}
          </Button>
        )}
      </div>
      {flagged.length > 0 && (
        <div className="border border-slate-100 rounded-md max-h-72 overflow-y-auto divide-y divide-slate-100">
          {flagged.map(it => (
            <div key={it.id} className="p-2 text-xs flex items-start gap-2" data-testid={`${testId}-row-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">{it.label || it.subject || it.id}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {(reports[it.id]?.warnings || []).slice(0, 3).join(' · ')}
                  {(reports[it.id]?.warnings || []).length > 3 && ` · +${reports[it.id].warnings.length - 3} more`}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => fixOneAuto(it)} className="h-6 px-2 text-[10px]" data-testid={`${testId}-row-${it.id}-autofix`}>
                <Wand2 className="w-2.5 h-2.5 mr-0.5" /> Auto
              </Button>
              <Button variant="ghost" size="sm" onClick={() => fixOneAi(it)} className="h-6 px-2 text-[10px] text-violet-700" data-testid={`${testId}-row-${it.id}-rewrite`}>
                <Sparkles className="w-2.5 h-2.5 mr-0.5" /> AI
              </Button>
              {onSkip && (
                <Button variant="ghost" size="sm" onClick={() => onSkip(it.id)} className="h-6 px-2 text-[10px] text-rose-600" data-testid={`${testId}-row-${it.id}-skip`}>
                  Skip
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
